import {useQuery} from '@tanstack/react-query';
import {Box, CircularProgress, Alert} from '@mui/material';
import apiCall from '../../../../utils/api';
import Indicator from '../../components/Indicator';
import Section from '../../components/Section';
import {IDashboardData, SurveySlug} from '../../utils/types';
import {buildApiKey, serializeSlug, scaleToPercentage} from '../../utils/helpers';

interface Props {
    container: string;
    uoaId: string;
    surveyId: string;
    aggiornamentoSurveyId: string;
    technicalComplexityTitle?: string;
}

const buildSurveySlugs = (
    surveyId: number,
    aggiornamentoSurveyId: number,
    technicalComplexityTitle?: string,
): SurveySlug[] => [
    {
        numerator: {survey_id: surveyId, question: 'question14'},
        denominator: {survey_id: surveyId, question: 'question5'},
        operation: 'sum',
        displayAsPercentage: true,
        key: 'surface_ratio',
        roman: 'I',
        title: 'Percentuale di superficie oggetto di rigenerazione a confronto con la media',
        tooltip:
            'Il grafico mostra la percentuale della superficie del bene oggetto di rigenerazione per il vostro progetto, a confronto con la media dello stesso valore in tutti i progetti. Il valore è ottenuto come rapporto tra la superficie che il progetto prevede di rigenerare e la superficie totale del bene oggetto di rigenerazione.',
    },
    {
        numerator: {survey_id: surveyId, question: 'question17'},
        denominator: {survey_id: surveyId, question: 'question14'},
        operation: 'sum',
        displayAsPercentage: true,
        key: 'external_surface_ratio',
        roman: 'II',
        title: 'Percentuale di superficie esterna oggetto di rigenerazione a confronto con la media',
        tooltip:
            'Il grafico mostra la percentuale della superficie esterna del bene oggetto di rigenerazione per il vostro progetto, a confronto con la media dello stesso valore in tutti i progetti. Il valore è ottenuto come rapporto tra la superficie esterna che il progetto prevede di rigenerare e la superficie complessiva oggetto degli interventi.',
    },
    {
        numerator: {survey_id: surveyId, question: 'question15'},
        operation: 'count',
        key: 'technical_complexity',
        roman: 'III',
        title: technicalComplexityTitle ?? "Complessità tecnica dell'intervento a confronto con la media",
        tooltip:
            "Il grafico mostra la varietà delle attività realizzate nell'ambito del vostro intervento, a confronto con la media dello stesso valore in tutti i progetti. Il valore medio è calcolato come il rapporto tra la somma totale delle tipologie diverse di attività realizzate in tutti gli interventi e il numero totale di interventi.",
    },
    {
        numerator: {survey_id: aggiornamentoSurveyId, question: 'question22'},
        operation: 'sum',
        key: 'work_progress',
        roman: 'IV',
        title: 'Percentuale di avanzamento dei lavori a confronto con la media',
        tooltip:
            'Il grafico mostra la percentuale di avanzamento dei lavori di riqualificazione del vostro progetto, a confronto con la media calcolata su tutti i progetti. Il valore medio è ottenuto dividendo la somma delle percentuali di avanzamento rilevate per ciascun progetto per il numero totale di progetti.',
        perUoa: true,
        perUoaLatest: true,
        fallbacks: [{
            operation: 'sum',
            numerator: {survey_id: surveyId, question: 'question22'},
        }],
    },
    {
        numerator: {survey_id: aggiornamentoSurveyId, question: 'question31'},
        operation: 'sum',
        key: 'citizen_participation',
        roman: 'V',
        title: 'Partecipazione cittadina a confronto con la media',
        tooltip:
            'Il grafico mostra il numero di cittadini coinvolti nelle attività partecipative relative al vostro progetto, a confronto con la media calcolata su tutti i progetti. Il valore medio è ottenuto dividendo la somma dei cittadini coinvolti in tutti i progetti per il numero totale di progetti che hanno attivato attività partecipative.',
        perUoa: true,
        perUoaLatest: true,
        fallbacks: [{
            operation: 'sum',
            numerator: {survey_id: surveyId, question: 'question31'},
        }],
    },
];

const LuoghiDaRigenerare = ({
    container,
    uoaId,
    surveyId,
    aggiornamentoSurveyId,
    technicalComplexityTitle,
}: Props) => {
    const {data, isLoading, isError} = useQuery<IDashboardData>({
        queryKey: [
            'luoghi-dashboard1',
            container,
            uoaId,
            surveyId,
            aggiornamentoSurveyId,
        ],
        queryFn: async () => {
            const slugs = buildSurveySlugs(
                Number(surveyId),
                Number(aggiornamentoSurveyId),
                technicalComplexityTitle,
            );

            const res = await apiCall({
                url: `containers/${container}/uoa/${uoaId}/dashboards/fsv`,
                method: 'POST',
                data: {
                    question_slugs: slugs.map(serializeSlug),
                },
            });

            const result = {} as IDashboardData;
            for (const slug of slugs) {
                const apiKey = buildApiKey(slug);
                const value = res.data[apiKey] ?? {uoa: 0, average: 0};
                result[slug.key] = slug.displayAsPercentage ? scaleToPercentage(value) : value;
            }
            return result;
        },
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    const surveySlugs = buildSurveySlugs(
        Number(surveyId),
        Number(aggiornamentoSurveyId),
        technicalComplexityTitle,
    );

    const sections = [
        {
            title: 'Superficie rigenerata su totale del luogo',
            keys: ['surface_ratio', 'external_surface_ratio'],
            maxOverride: 100,
        },
        {
            title: "Complessità tecnica dell'intervento",
            keys: ['technical_complexity'],
            isCount: true,
        },
        {
            title: 'Avanzamento dei lavori',
            keys: ['work_progress'],
            maxOverride: 100,
        },
        {
            title: 'Partecipazione cittadina',
            keys: ['citizen_participation'],
            isCount: true,
        },
    ];

    return (
        <Box>
            {sections.map((section) => (
                <Section key={section.title} title={section.title}>
                    {surveySlugs
                        .filter((s) => section.keys.includes(s.key))
                        .map((slug) => (
                            <Indicator
                                key={slug.key}
                                roman={slug.roman}
                                title={slug.title}
                                tooltip={slug.tooltip}
                                data={data[slug.key]}
                                maxOverride={section.maxOverride}
                                isCount={section.isCount}
                            />
                        ))}
                </Section>
            ))}
        </Box>
    );
};

export default LuoghiDaRigenerare;
